import { Card } from './Card.js';
import { PileType } from '../types/index.js';

export class GameRules {
  /**
   * Check if a card can be moved to a tableau pile
   */
  static canMoveToTableau(card: Card, targetPile: Card[]): boolean {
    if (targetPile.length === 0) {
      // Empty tableau: only King allowed
      return card.canPlaceOnEmptyTableau();
    }

    const topCard = targetPile[targetPile.length - 1];

    // Target card must be face-up
    if (!topCard.isFaceUp) {
      return false;
    }

    // Check Klondike tableau rules: descending rank, alternating colors
    return card.canStackOnTableau(topCard);
  }

  /**
   * Check if a card can be moved to a foundation pile
   */
  static canMoveToFoundation(card: Card, targetPile: Card[]): boolean {
    if (targetPile.length === 0) {
      // Empty foundation: only Ace allowed
      return card.canStackOnFoundation(null);
    }

    const topCard = targetPile[targetPile.length - 1];

    // Check Klondike foundation rules: ascending rank, same suit
    return card.canStackOnFoundation(topCard);
  }

  /**
   * Check if multiple cards can be moved together from tableau
   * All cards in the sequence must be face-up and in valid tableau order
   */
  static canMoveTableauSequence(cards: Card[]): boolean {
    if (cards.length === 0) {
      return false;
    }

    // All cards must be face-up
    if (!cards.every(card => card.isFaceUp)) {
      return false;
    }

    // Check that the sequence is valid (descending rank, alternating colors)
    for (let i = 1; i < cards.length; i++) {
      if (!cards[i].canStackOnTableau(cards[i - 1])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Validate a complete move
   */
  static isValidMove(
    cards: Card[],
    _fromPile: PileType,
    _fromPileCards: Card[],
    toPile: PileType,
    toPileCards: Card[]
  ): boolean {
    if (cards.length === 0) {
      return false;
    }

    const movingCard = cards[0];

    // Can't move face-down cards (except as part of valid tableau sequence)
    if (!movingCard.isFaceUp) {
      return false;
    }

    // Validate based on destination pile type
    switch (toPile) {
      case PileType.TABLEAU:
        // For tableau, can move multiple cards if they form a valid sequence
        if (cards.length > 1) {
          return this.canMoveTableauSequence(cards) &&
                 this.canMoveToTableau(movingCard, toPileCards);
        }
        return this.canMoveToTableau(movingCard, toPileCards);

      case PileType.FOUNDATION:
        // Can only move one card at a time to foundation
        if (cards.length > 1) {
          return false;
        }
        return this.canMoveToFoundation(movingCard, toPileCards);

      case PileType.WASTE:
      case PileType.STOCK:
        // Can't move cards to waste or stock
        return false;

      default:
        return false;
    }
  }

  /**
   * Get the card that should be auto-flipped after a move
   * Returns the card if it should be flipped, null otherwise
   */
  static getAutoFlipCard(pile: Card[]): Card | null {
    if (pile.length === 0) {
      return null;
    }

    const topCard = pile[pile.length - 1];
    if (!topCard.isFaceUp) {
      return topCard;
    }

    return null;
  }

  /**
   * Check if the game is won
   * All 4 foundations must have 13 cards (complete suit)
   */
  static isGameWon(foundations: Card[][]): boolean {
    if (foundations.length !== 4) {
      return false;
    }

    return foundations.every(foundation => foundation.length === 13);
  }

  /**
   * Check if auto-complete is possible
   * All tableau cards must be face-up
   */
  static canAutoComplete(tableau: Card[][]): boolean {
    for (const pile of tableau) {
      for (const card of pile) {
        if (!card.isFaceUp) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Find the best foundation for a card (same suit)
   */
  static findFoundationForCard(card: Card, foundations: Card[][]): number | null {
    for (let i = 0; i < foundations.length; i++) {
      const foundation = foundations[i];

      if (this.canMoveToFoundation(card, foundation)) {
        // If foundation is empty, only accept if it's the right "slot" for this suit
        // or if any empty slot (we'll use the first empty)
        if (foundation.length === 0) {
          return i;
        }

        // Check if this foundation is for the same suit
        const topCard = foundation[foundation.length - 1];
        if (topCard.suit === card.suit) {
          return i;
        }
      }
    }

    return null;
  }
}
